/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Handle } from 'reactflow';
import { useTranslation } from 'react-i18next';

// Component imports
import { Space, Typography } from 'antd';
import NodeWrapper from '../../NodeWrapper';
import { GraphContext } from '../../FlowGraph';
import { GRAPH_LAYOUTS } from '../../../../constants/flowGraph';
import LimitedConnectionsHandle from '../../LimitedConnectionsHandle';

// Misc imports
import { NODE_TYPES_PROPERTIES } from '../../../../constants/flowGraph';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';
import { INTEGRATIONS } from '../../../../constants/integrations';

// Constant declarations
const { Text } = Typography;

const LlmNode = ({ id, type, data, dragging, selected, isConnectable }) => {
  const nodeProps = NODE_TYPES_PROPERTIES[type];
  const { t } = useTranslation();
  const { graphDirection } = useContext(GraphContext);
  const { currentBotLanguage } = useBotLanguage();

  const renderNodeInternal = (data) => {
    if (data?.friendlyName) {
      return `${data?.friendlyName}`;
    }

    if (data?.llm?.provider) {
      return t(Object.values(INTEGRATIONS).find((integration) => integration.type === data.llm.provider).type
        , { context: 'type' });
    }

    return t('Click to edit LLM node');
  };

  const NodeContents = useMemo(() => {
    return (
      <Fragment>
        <Handle type="target" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'top' : 'left'} isConnectable={isConnectable} />

        <Space size={6} className="node-header">
          {nodeProps.icon}
          {nodeProps.label}
        </Space>

        <Text type="secondary" ellipsis>
          {renderNodeInternal(data || {})}
        </Text>

        <LimitedConnectionsHandle id={id} type="source" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'bottom' : 'right'} allowedOutgoingEdgesCount={1} />
      </Fragment>
    );
  }, [data, nodeProps, graphDirection, currentBotLanguage]);

  return (
    <NodeWrapper
      id={id}
      type={type}
      data={data}
      children={NodeContents}
      dragging={dragging}
      selected={selected}
    />
  );
};

// https://reactflow.dev/docs/api/nodes/node-options/
LlmNode.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  isConnectable: PropTypes.bool,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  type: PropTypes.string,
  xPos: PropTypes.number,
  yPos: PropTypes.number
};

export default memo(LlmNode);
